<template>
  <div>
    <v-form>
      <h2 class="mb-3">Density Lab Part II - Results</h2>

      <p class="mb-5">
        <b>Note.</b> All literature densities for the possible unknown materials can be found on
        this
        <a
          href="https://drive.google.com/file/d/1nwHuNVovm1MR9TPGeh7sIcrTJm8-O2G_/view"
          rel="noopener noreferrer"
          target="_blank"
          >reference sheet</a
        >.
      </p>

      <h3 class="mb-3">a) Regularly-Shaped Object</h3>

      <p class="mb-2">i) What was the ID letter/number of your unknown object?</p>

      <v-text-field v-model="inputs.idNum1" class="mb-0" outlined dense :disabled="!allowEditing" />

      <v-simple-table class="mb-5" style="max-width: 493px">
        <thead>
          <tr>
            <td style="text-align: left; font-weight: bold" class="pl-2">Property</td>
            <td style="text-align: center; font-weight: bold">Value</td>
          </tr>
          <tr v-for="option in options1" :key="option.input1">
            <td class="my-1 py-0">
              <stemble-latex :content="option.text1" />
            </td>
            <td class="centered-input my-1 py-0">
              <calculation-input v-model="inputs[option.input1]" dense :disabled="!allowEditing" />
            </td>
          </tr>
        </thead>
      </v-simple-table>

      <p class="mb-n4">v) What is the identity of your regularly-shaped unknown object?</p>

      <v-radio-group v-model="inputs.identity1" :disabled="!allowEditing" class="mb-0 pl-6">
        <v-radio
          v-for="option in optionsUnk1Shuffled"
          :key="'pt-1-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>

      <h3 class="mt-4 mb-3">b) Irregularly-Shaped Object</h3>

      <p class="mb-2">i) What was the ID letter/number of your unknown object?</p>

      <v-text-field v-model="inputs.idNum2" class="mb-0" outlined dense :disabled="!allowEditing" />

      <v-simple-table class="mb-5" style="max-width: 493px">
        <thead>
          <tr>
            <td style="text-align: left; font-weight: bold" class="pl-2">Property</td>
            <td style="text-align: center; font-weight: bold">Value</td>
          </tr>
          <tr v-for="option in options1" :key="option.input2">
            <td class="my-1 py-0">
              <stemble-latex :content="option.text2" />
            </td>
            <td class="centered-input my-1 py-0">
              <calculation-input v-model="inputs[option.input2]" dense :disabled="!allowEditing" />
            </td>
          </tr>
        </thead>
      </v-simple-table>

      <p class="mb-n4">v) What is the identity of your irregularly-shaped unknown object?</p>

      <v-radio-group v-model="inputs.identity2" :disabled="!allowEditing" class="mb-0 pl-6">
        <v-radio
          v-for="option in optionsUnk2Shuffled"
          :key="'pt-2-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>

      <h3 class="mt-4 mb-3">c) Liquid</h3>

      <p class="mb-2">i) What was the ID letter/number of your unknown object?</p>

      <v-text-field v-model="inputs.idNum3" class="mb-0" outlined dense :disabled="!allowEditing" />

      <v-simple-table class="mb-5" style="max-width: 493px">
        <thead>
          <tr>
            <td style="text-align: left; font-weight: bold" class="pl-2">Property</td>
            <td style="text-align: center; font-weight: bold">Value</td>
          </tr>
          <tr v-for="option in options1" :key="option.input3">
            <td class="my-1 py-0">
              <stemble-latex :content="option.text3" />
            </td>
            <td class="centered-input my-1 py-0">
              <calculation-input v-model="inputs[option.input3]" dense :disabled="!allowEditing" />
            </td>
          </tr>
        </thead>
      </v-simple-table>

      <p class="mb-n4">v) What is the identity of your unknown liquid?</p>

      <v-radio-group v-model="inputs.identity3" :disabled="!allowEditing" class="mb-0 pl-6">
        <v-radio
          v-for="option in optionsUnk3Shuffled"
          :key="'pt-3-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex.vue';
import CalculationInput from '../inputs/CalculationInput.vue';
import ChemicalLatex from '@/tasks/components/displayers/ChemicalLatex.vue';
import {seededShuffle} from '@/courses/utils/seededShuffle';

export default {
  name: 'OleMiss_DensityLab_ReportSheet2',
  components: {ChemicalLatex, StembleLatex, CalculationInput},
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        idNum1: null,
        mass1: null,
        vol1: null,
        density1: null,
        identity1: null,

        idNum2: null,
        mass2: null,
        vol2: null,
        density2: null,
        identity2: null,

        idNum3: null,
        mass3: null,
        vol3: null,
        density3: null,
        identity3: null,
      },
      options1: [
        {
          text1: 'ii) Mass (g)',
          text2: 'ii) Mass (g)',
          text3: 'ii) Mass (g)',
          input1: 'mass1',
          input2: 'mass2',
          input3: 'mass3',
        },
        {
          text1: 'iii) Volume (cm³)',
          text2: 'iii) Volume (mL)',
          text3: 'iii) Volume (mL)',
          input1: 'vol1',
          input2: 'vol2',
          input3: 'vol3',
        },
        {
          text1: 'iv) Density (g/cm³)',
          text2: 'iv) Density (g/mL)',
          text3: 'iv) Density (g/mL)',
          input1: 'density1',
          input2: 'density2',
          input3: 'density3',
        },
      ],
      optionsUnk1: [
        {text: 'Aluminum', value: 'aluminum'}, // d = 2.7
        {text: 'Steel', value: 'steel'}, // d = 7.5 - 8.1
        {text: 'Lead', value: 'lead'}, // d = 11.3
      ],
      optionsUnk2: [
        {text: 'Polypropylene', value: 'pp'}, // d = 0.90-0.92
        {text: 'Polyvinyl chloride', value: 'pvc'}, // d = 1.3-1.5
        {text: 'Teflon', value: 'teflon'}, // d = 2.1-2.3
      ],
      optionsUnk3: [
        {text: '30% hydrogen peroxide', value: 'peroxide'}, // d = 1.11
        {text: 'Isopropyl alcohol', value: 'isopropanol'}, // d = 0.786
        {text: 'Water', value: 'water'}, // d = 1.00
      ],
    };
  },
  computed: {
    seed() {
      return this.$inertia.getSeed();
    },
    optionsUnk1Shuffled() {
      return seededShuffle(this.optionsUnk1, this.seed);
    },
    optionsUnk2Shuffled() {
      return seededShuffle(this.optionsUnk2, this.seed);
    },
    optionsUnk3Shuffled() {
      return seededShuffle(this.optionsUnk3, this.seed);
    },
  },
};
</script>
